import type { CockpitSnapshot, FunnelReport, Rate } from './types';

const rate = (numerator: number, denominator: number): Rate => ({
  numerator,
  denominator,
  rate: denominator ? numerator / denominator : null,
  ci: denominator ? { low: Math.max(0, numerator / denominator - 0.008), high: numerator / denominator + 0.008, level: 0.9 } : null,
});

const funnel = (group_by: string, rows: Array<[string, number, number, number, number, number]>): FunnelReport => ({
  group_by,
  metric: 'meeting_held',
  buckets: rows.map(([bucket, emails, replies, qualified, booked, held]) => ({
    bucket,
    power: emails >= 1000 ? 'crownable' : emails >= 500 ? 'judgeable' : 'underpowered',
    counts: {
      emails_sent: emails,
      leads_contacted: Math.round(emails * 0.72),
      reply: replies,
      qualified_reply: qualified,
      meeting_booked: booked,
      meeting_held: held,
      bounced: Math.round(emails * 0.014),
      complained: Math.round(emails * 0.0004),
    },
    revenue_usd: held * 1800,
    rates: {
      reply: rate(replies, emails),
      reply_per_contact: rate(replies, Math.round(emails * 0.72)),
      meeting_held: rate(held, emails),
    },
  })),
  comparison: {
    leader: rows[0]?.[0] ?? null,
    decisive: false,
    verdicts: rows.map(([bucket, emails]) => ({
      bucket,
      verdict: emails < 500 ? 'hold' : bucket === rows[0]?.[0] ? 'leader' : 'continue',
      reason: emails < 500 ? `${emails} sends; needs 500 before this can be judged` : '90% intervals still overlap.',
    })),
  },
  warnings: [],
});

const byCampaign = funnel('campaign', [
  ['Roofing owners · warm proof', 1460, 64, 26, 15, 11],
  ['Dental groups · hiring signal', 820, 31, 10, 6, 4],
  ['Home services · operator letter', 314, 15, 7, 3, 2],
]);
const bySource = funnel('source_method', [
  ['apify-maps', 1140, 50, 22, 12, 9],
  ['apollo', 980, 38, 13, 8, 5],
  ['conference-list', 474, 22, 8, 4, 3],
]);
const byPlatform = funnel('platform', [
  ['edgemail', 1320, 58, 24, 14, 10],
  ['instantly', 1274, 52, 19, 10, 7],
]);
const byNiche = funnel('niche', [
  ['Roofing companies · 5–50 employees', 1460, 64, 26, 15, 11],
  ['Dental groups · 3+ locations', 820, 31, 10, 6, 4],
  ['HVAC installers · growth markets', 314, 15, 7, 3, 2],
]);

export const previewData: CockpitSnapshot = {
  generated_at: '2026-08-10T14:22:00Z',
  session: {
    user: { email: 'g@edge-mail.internal', name: 'G', role: 'admin' },
    workspace: { id: 'preview', slug: 'edge-mail', name: 'Edge Mail' },
  },
  today: {
    headline: {
      qualified_meetings_held: 17,
      meetings_booked: 24,
      positive_replies: 43,
      total_replies: 110,
      leads_contacted: 1868,
      emails_sent: 2594,
      cost_usd: 2842.6,
      cost_per_reply: 25.84,
      cost_per_held_meeting: 167.21,
      held_rate: rate(17, 2594),
    },
    work_items: [
      {
        id: 'w1',
        priority: 'critical',
        category: 'Deliverability',
        title: 'Pause two mailboxes before the next send window',
        detail: 'Complaint rate on edgegrowth.co is above the configured 0.30% stop threshold.',
        href: '/deliverability',
        due_at: null,
      },
      {
        id: 'w2',
        priority: 'urgent',
        category: 'Reply',
        title: 'Maya Chen at Summit Roofing is interested',
        detail: 'This sounds relevant. Can you show me how this would work across our three locations?',
        href: '/inbox?reply=r1',
        due_at: '2026-08-10T14:26:00Z',
      },
      {
        id: 'w3',
        priority: 'high',
        category: 'Approval',
        title: 'Approve the Roofing owners list gate',
        detail: '17 of 20 sampled leads match the ICP. 1,180 records and $236 are affected.',
        href: '/today?approval=a1',
        due_at: null,
      },
      {
        id: 'w4',
        priority: 'normal',
        category: 'Experiment',
        title: 'Source test is ready for a CONTINUE review',
        detail: 'Apify Maps leads on held meetings, but intervals still overlap Apollo.',
        href: '/experiments',
        due_at: null,
      },
    ],
    bottleneck: {
      stage: 'booking',
      title: 'Reply-to-booking handoff is the constraint',
      detail: 'Qualified replies are arriving, but 44% are waiting more than five minutes for a human response.',
      confidence: 'high',
      next_action: 'Clear the interested-reply queue and assign an SLA owner before increasing send volume.',
    },
    data_quality: {
      freshness: 'current',
      completeness: 'good',
      warnings: ['One GHL sync is 3 hours behind; today’s held-meeting count may be incomplete.'],
    },
  },
  campaigns: [
    {
      id: 'c1',
      label: 'Roofing owners · warm proof',
      status: 'active',
      platform: 'edgemail',
      source_method: 'apify-maps',
      niche: 'Roofing companies · 5–50 employees',
      current_phase: 'experiment',
      workflow_status: 'active',
      completed_steps: 6,
      total_steps: 8,
      pending_approvals: 1,
      created_at: '2026-07-22T13:00:00Z',
    },
    {
      id: 'c2',
      label: 'Dental groups · hiring signal',
      status: 'draft',
      platform: 'instantly',
      source_method: 'apollo',
      niche: 'Dental groups · 3+ locations',
      current_phase: 'copy',
      workflow_status: 'active',
      completed_steps: 4,
      total_steps: 8,
      pending_approvals: 0,
      created_at: '2026-08-02T16:30:00Z',
    },
  ],
  inbox: [
    {
      id: 'r1', task_id: 'rt1', received_at: '2026-08-10T14:21:00Z', subject: 'Re: Maya',
      preview: 'This sounds relevant. Can you show me how this would work across our three locations?',
      classification: 'interested', confidence: 0.96, review_state: 'confirmed', priority: 'urgent',
      task_status: 'open', scenario: 'interested', sla_due_at: '2026-08-10T14:26:00Z', owner_email: null,
      lead_name: 'Maya Chen', company: 'Summit Roofing', campaign: 'Roofing owners · warm proof',
    },
    {
      id: 'r2', task_id: 'rt2', received_at: '2026-08-10T13:48:00Z', subject: 'Re: quick question',
      preview: 'We already use another agency for outbound. What is different about your approach?',
      classification: 'objection', confidence: 0.91, review_state: 'confirmed', priority: 'high',
      task_status: 'drafting', scenario: 'already_use_competitor', sla_due_at: '2026-08-10T14:10:00Z', owner_email: 'g@edge-mail.internal',
      lead_name: 'Andre Lewis', company: 'Crown Dental Partners', campaign: 'Dental groups · hiring signal',
    },
    {
      id: 'r3', task_id: null, received_at: '2026-08-10T12:02:00Z', subject: 'Out of office',
      preview: 'I am away until August 17. Please contact my operations manager for urgent matters.',
      classification: 'ooo', confidence: 0.88, review_state: 'none', priority: 'normal',
      task_status: 'open', scenario: 'ooo', sla_due_at: null, owner_email: null,
      lead_name: 'Jordan Patel', company: 'Northstar Exteriors', campaign: 'Roofing owners · warm proof',
    },
  ],
  approvals: [
    {
      id: 'a1', campaign_id: 'c1', campaign: 'Roofing owners · warm proof', action_type: 'list_gate',
      title: 'Approve list quality gate', summary: '17 of 20 sampled leads match the approved ICP.', risk: 'medium',
      affected_records: 1180, estimated_cost_usd: 236, diff: { before: '100 sampled leads', after: '1,180 verified leads' },
      evidence: ['20 records reviewed', '17 qualified', '2 wrong size', '1 wrong geography'], status: 'pending', version: 2,
      requested_by: 'Outbound Brain', requested_at: '2026-08-10T13:10:00Z',
    },
    {
      id: 'a2', campaign_id: 'c2', campaign: 'Dental groups · hiring signal', action_type: 'copy_gate',
      title: 'Approve two copy hypotheses', summary: 'Both variants passed claims and personalization checks.', risk: 'low',
      affected_records: 10, estimated_cost_usd: 1.84, diff: { variants: ['Hiring signal', 'Operations proof'] },
      evidence: ['Analyzer 87/100', 'No unsupported claims', '10 rendered previews'], status: 'pending', version: 1,
      requested_by: 'Outbound Brain', requested_at: '2026-08-10T11:44:00Z',
    },
  ],
  experiments: [
    {
      id: 'e1', label: 'Apify Maps vs Apollo', campaign: 'Roofing owners · warm proof',
      hypothesis: 'High-intent local business records will produce more held meetings per dollar.',
      test_dimension: 'source_method', primary_outcome: 'meeting_held', mde: 0.006, minimum_n: 500, crown_n: 1000,
      status: 'active', recommendation: 'continue', sample_size: 980,
      rationale: 'Apify leads on the point estimate, but the 90% intervals overlap.',
    },
    {
      id: 'e2', label: 'Hiring signal vs operations proof', campaign: 'Dental groups · hiring signal',
      hypothesis: 'A current hiring signal creates more qualified intent than generic operational proof.',
      test_dimension: 'copy', primary_outcome: 'meeting_held', mde: 0.008, minimum_n: 500, crown_n: 1000,
      status: 'draft', recommendation: 'hold', sample_size: 0,
      rationale: 'Preflight and copy approval must complete before assignment.',
    },
  ],
  leads: [
    {
      id: 'lb1', campaign: 'Roofing owners · warm proof', source_method: 'apify-maps', tier: 'standard', status: 'approved',
      total_leads: 1180, cost_usd: 236, sample_size: 20, sample_qualified: 17, sample_passed: true, created_at: '2026-08-08T09:00:00Z',
    },
    {
      id: 'lb2', campaign: 'Dental groups · hiring signal', source_method: 'apollo', tier: 'standard', status: 'sampling',
      total_leads: 640, cost_usd: 0, sample_size: 12, sample_qualified: 8, sample_passed: false, created_at: '2026-08-09T15:00:00Z',
    },
  ],
  deliverability: {
    health: {
      generated_at: '2026-08-10T14:22:00Z',
      feeds: [
        { source: 'edgemail', scope_ref: 'primary', health: 'ok', stale_hours: 0.08, consecutive_failures: 0, last_success_at: '2026-08-10T14:17:00Z', last_error: null },
        { source: 'instantly', scope_ref: 'primary', health: 'ok', stale_hours: 0.16, consecutive_failures: 0, last_success_at: '2026-08-10T14:12:00Z', last_error: null },
        { source: 'ghl', scope_ref: 'meetings', health: 'degraded', stale_hours: 3.1, consecutive_failures: 2, last_success_at: '2026-08-10T11:16:00Z', last_error: 'Rate limited; next retry queued.' },
      ],
      deliverability: {
        overall: { bucket: 'overall', bounce: rate(36, 2594), complaint: rate(2, 2594), bounce_flag: 'ok', complaint_flag: 'ok', pause_not_ruled_out: false },
        by_platform: [
          { bucket: 'edgemail', bounce: rate(15, 1320), complaint: rate(1, 1320), bounce_flag: 'ok', complaint_flag: 'ok', pause_not_ruled_out: false },
          { bucket: 'instantly', bounce: rate(21, 1274), complaint: rate(1, 1274), bounce_flag: 'ok', complaint_flag: 'ok', pause_not_ruled_out: false },
        ],
      },
      suppressions: { total: 42, by_reason: [{ reason: 'unsubscribe', n: 29 }, { reason: 'hard_bounce', n: 13 }] },
      review_queue: 3,
      attribution_coverage: { leads_with_source_method: rate(1842, 1868), campaigns_with_platform: rate(2, 2) },
      warnings: ['feed ghl/meetings is degraded: Rate limited; next retry queued.'],
    },
    domains: [
      { id: 'd1', domain: 'edgegrowth.co', status: 'paused', warmup_started_at: '2026-07-01T00:00:00Z', daily_cap: 25, provider: 'Google Workspace', failing_checks: 0, last_checked_at: '2026-08-10T13:55:00Z' },
      { id: 'd2', domain: 'tryedgeops.com', status: 'ready', warmup_started_at: '2026-07-12T00:00:00Z', daily_cap: 30, provider: 'Microsoft 365', failing_checks: 0, last_checked_at: '2026-08-10T13:54:00Z' },
      { id: 'd3', domain: 'meetedge.co', status: 'warming', warmup_started_at: '2026-08-04T00:00:00Z', daily_cap: 10, provider: 'Google Workspace', failing_checks: 0, last_checked_at: '2026-08-10T13:52:00Z' },
    ],
    mailboxes: [
      { id: 'm1', email: 'g@edgegrowth.co', domain: 'edgegrowth.co', provider: 'edgemail', status: 'paused', daily_cap: 25, last_sync_at: '2026-08-10T14:17:00Z' },
      { id: 'm2', email: 'nick@tryedgeops.com', domain: 'tryedgeops.com', provider: 'instantly', status: 'ready', daily_cap: 30, last_sync_at: '2026-08-10T14:12:00Z' },
      { id: 'm3', email: 'hello@meetedge.co', domain: 'meetedge.co', provider: 'instantly', status: 'warming', daily_cap: 10, last_sync_at: '2026-08-10T14:12:00Z' },
    ],
  },
  playbooks: {
    routines: [
      { id: 'j1', label: 'Daily reply classification', kind: 'classification', mode: 'routine', status: 'complete', processed: 23, total: 23, estimated_cost_usd: 1.2, actual_cost_usd: 1.08, retry_count: 0, error_message: null, created_at: '2026-08-10T06:00:00Z' },
      { id: 'j2', label: 'Roofing list enrichment test', kind: 'enrichment', mode: 'test', status: 'complete', processed: 10, total: 10, estimated_cost_usd: 0.94, actual_cost_usd: 0.88, retry_count: 0, error_message: null, created_at: '2026-08-09T18:20:00Z' },
      { id: 'j3', label: 'GHL held-meeting sync', kind: 'crm_sync', mode: 'routine', status: 'failed', processed: 41, total: 48, estimated_cost_usd: 0, actual_cost_usd: 0, retry_count: 2, error_message: 'Provider rate limit. No records were lost.', created_at: '2026-08-10T11:15:00Z' },
    ],
    policies: [
      { id: 'p1', label: 'US B2B · standard volume', jurisdiction: 'US', provider: null, volume_class: 'standard', status: 'active', effective_at: '2026-08-01T00:00:00Z' },
      { id: 'p2', label: 'Gmail bulk sender', jurisdiction: 'US', provider: 'gmail', volume_class: 'bulk', status: 'active', effective_at: '2026-08-01T00:00:00Z' },
    ],
    sender_profiles: [
      { id: 'sp1', name: 'G Wood', email: 'g@edge-mail.internal', version: 3, approved_at: '2026-08-07T17:00:00Z', approved_facts: 18, approved_claims: 6 },
    ],
  },
  insights: {
    by_campaign: byCampaign,
    by_source: bySource,
    by_platform: byPlatform,
    by_niche: byNiche,
    objections: {
      buckets: [
        { bucket: 'apify-maps', classified_replies: 50, objections: [
          { objection_type: 'already_have_vendor', share: rate(11, 50), example: 'We already have an agency doing this.' },
          { objection_type: 'bad_timing', share: rate(8, 50), example: 'Circle back after our busy season.' },
          { objection_type: 'trust_credibility', share: rate(6, 50), example: 'Can you show me a roofing example?' },
        ] },
      ],
      warnings: [],
    },
    total_cost_usd: 2842.6,
  },
  notifications: [
    { id: 'n1', severity: 'critical', category: 'deliverability', title: 'Two mailboxes paused', body: 'Complaint policy triggered before the next send window.', created_at: '2026-08-10T14:15:00Z' },
  ],
  audit: [
    { id: 'au1', actor_email: 'g@edge-mail.internal', actor_role: 'admin', action: 'approved', entity_type: 'copy_gate', entity_id: 'a0', reason: 'Claims and previews verified.', occurred_at: '2026-08-10T12:30:00Z' },
    { id: 'au2', actor_email: 'Outbound Brain', actor_role: 'operator', action: 'proposed', entity_type: 'list_gate', entity_id: 'a1', reason: '17 of 20 leads passed ICP review.', occurred_at: '2026-08-10T13:10:00Z' },
  ],
  members: [
    { id: 'u1', email: 'g@edge-mail.internal', display_name: 'G', role: 'admin', status: 'active' },
    { id: 'u2', email: 'ops@edge-mail.internal', display_name: 'Operator', role: 'operator', status: 'active' },
  ],
};
